/* Electron工具类*/

import dayjs from 'dayjs'

const fs = getFsModule()
/**
 * 判断是否运行在Electron中
 * @returns {boolean}
 */
export function isRunningInElectron() {
  const userAgent = navigator.userAgent.toLowerCase()
  return userAgent.indexOf(' electron/') > -1 || window.electron
}

/**
 * 是否离线
 * @return {boolean} true: '离线'；false: '在线'
 */
export function fetchOffline() {
  return window.offlineMode === '1'
}

/**
 * 运行环境
 * @return {string} web: '网页'；onlineElectron: '在线Electron'；offlineElectron：'离线Electron'
 */
export function isRunningEnvironment() {
  let environment = 'web'
  if (isRunningInElectron()) environment = fetchOffline() ? 'onlineElectron' : 'offlineElectron'
  return environment
}

/**
 * 转换本地文件地址 -- 本地转临时
 * @param url 本地路径
 * @return {Promise<string>} 临时文件地址
 */
export async function readLocalFile(url) {
  if (!fs.existsSync(url)) return
  const uint8Array = await fs.readFileData(url)
  if (!uint8Array) return ''
  const blob = new Blob([uint8Array], { type: 'application/octet-stream' })
  return URL.createObjectURL(blob)
}

/**
 * 转换本地文件地址 -- 本地转临时
 * @param url 本地路径
 * @return {Promise<string>} 临时文件地址
 */
export async function readLocalFileBuffer(url) {
  if (!fs.existsSync(url)) return
  const uint8Array = await fs.readFileData(url)
  return new Blob([uint8Array])
}

/**
 * 写文件
 * @param file 文件
 * @param url 写入地址
 * @return {Promise<any>}
 */
export async function writeFile(file, url) {
  return await fs.writeFileData({ file, url })
}

/**
 * 复制文件
 * @param src 源文件路径
 * @param url 复制操作将创建的目标文件路径（包含文件名）
 */
export async function copyFileData({ src, url }) {
  return await fs.copyFileData({ src, url })
}

/**
 * 解压 zip 文件
 * @param filePath 压缩包-源文件路径
 * @param destinationPath 解压到的目的路径
 * @return {Promise<any>}
 */
export async function decompression({ filePath, destinationPath }) {
  return await fs.decompression({ filePath, destinationPath })
}

/**
 * 获取目录状态-- empty：路径不存在；directory：文件夹；file：文件
 * @param path String 压缩包-源文件路径
 * @return {Promise<any>}
 */
export async function fetchFileStatus(path) {
  return await fs.fileStata(path)
}
/**
 * 获取目录下级目录
 * @param path String 压缩包-源文件路径
 * @return {Promise<any>}
 */
export async function fetchFileNames(path) {
  return fs.readdirSync(path, 'utf8')
}
/**
 * 解压目录下所有zip压缩包
 * @param directory Object 目录路径
 * @param map Object 书籍id-书籍信息映射
 * @return {Promise<void>}
 */
export async function decompressionAllFiles(directory, map) {
  // 获取目录下的所有文件
  const files = fs.readdirSync(directory)
  const flag = fetchDirectoryName(directory) === 'book_package'
  try {
    // 遍历所有文件
    for (const file of files) {
      let filePath = `${directory}/${file}`
      const fileName = fetchFileName(file)
      const destinationPath = `${directory}/${fileName}`
      if (flag) {
        const bookInfo = fetchBookInfo(fileName)
        map[bookInfo.bookId] = {
          ...bookInfo,
          filePath: destinationPath
        }
      }
      // 判断是否为zip文件
      if (file.endsWith('.zip')) {
        // 执行解压命令
        await decompression({ filePath, destinationPath })
        // 删除原始zip文件
        fs.unlinkSync(filePath)
        filePath = destinationPath
      }
      const fileStatus = await fs.fileStata(filePath)
      // 判断是否为目录
      if (fileStatus === 'directory') {
        // 递归调用解压函数
        await decompressionAllFiles(filePath, map)
      }
    }
  } catch (e) {
    console.error(e)
  }
}
/**
 * 移动文件
 * @param path 源文件路径
 * @param newPath 目标路径
 * @return {Promise<any>}
 */
export async function renameFile(path, newPath) {
  return await fs.renameFileFn({ path, newPath })
}

/**
 * 删除文件
 * @param path 文件路径
 * @return {Promise<any>}
 */
export async function removeFile(path) {
  return await fs.removeFileFn(path)
}

/**
 * 创建目录
 * @param path 目录路径
 * @return {Promise<any>}
 */
export async function mkdir(path) {
  return await fs.mkdirFn(path)
}
export function getFsModule() {
  if (isRunningInElectron()) {
    return window.electron.fs
  }
  return {}
}

/**
 * 读取资源文件夹目录
 * @param url 目录路径
 * @param list 资源数据
 * @return {Promise<{file2PathMap: {}, name2PathMap: {}, list: (*|*[])}>}
 *  list：资源目录结构
 *  file2PathMap：文件路径映射
 *  name2PathMap：目录路径映射
 */
export async function readingFileDirectory(url, list = []) {
  const file2PathMap = {}
  const name2PathMap = {}
  const bookMap = {}
  const level = 1
  list = await readResource(url, file2PathMap, name2PathMap, bookMap, level)
  return { list, file2PathMap, name2PathMap, bookMap }
}

/**
 * 读取资源--递归调用
 * @param url String 目录路径
 * @param file2PathMap Object 文件-路径映射
 * @param name2PathMap Object  目录-路径映射
 * @param bookMap Object 书籍-书籍信息映射
 * @param level Number 层级
 * @return {Promise<unknown>}
 */
function readResource(url, file2PathMap, name2PathMap, bookMap, level) {
  return new Promise(resolve => {
    if (!fs.existsSync(url)) resolve(null)
    const isBook = fetchDirectoryName(url) === 'book_package'
    const fileNames = fs.readdirSync(url, 'utf8')
    // 如果资源包没有书籍，删掉这个资源包
    if (!fileNames.length) {
      if (isBook) {
        const parentPath = url.split('/book_package').slice(0, -1).join('')
        removeFile(parentPath).then(() => {
          resolve(null)
        })
      } else {
        resolve(null)
      }
    }
    const list = []
    let num = 0 // 记录当前目录列表处理结束数量
    fileNames.forEach(async name => {
      const filePath = url + '/' + name
      const obj = {
        key: name,
        url: filePath,
        type: '',
        level,
        childList: null
      }
      if (isBook) {
        const bookInfo = fetchBookInfo(name)
        bookMap[bookInfo.bookId] = {
          ...bookInfo,
          filePath
        }
      }
      name2PathMap[name] = filePath
      const fileStatus = await fs.fileStata(filePath)
      obj.type = fileStatus
      if (fileStatus === 'directory') {
        obj.childList = await readResource(filePath, file2PathMap, name2PathMap, bookMap, level + 1)
      } else if (fileStatus === 'file') {
        file2PathMap[name] = filePath
        const suffix = fetchSuffix(name)
        obj.suffix = suffix
        if (suffix === 'json') {
          obj.data = {
            ...fetchFile(filePath),
            packagePath: url
          }
          if (name === 'book_config.json') {
            obj.data.coverUrl = await readLocalFile(obj.data.packagePath + obj.data.coverPackageUrl)
          }
        }
      }
      list.push(obj)
      num += 1
      if (num >= fileNames.length) {
        resolve(list)
      }
    })
  })
}

/**
 * 解析书籍名目录
 * @param name 文件名
 * @return {string}
 */
export function fetchBookInfo(name) {
  const index = name.indexOf('_book_package_')
  const bookId = name.substring(0, index)
  const time = name.substring(index + 14, index + 29).replace(/_/, ' ')
  return {
    bookId,
    time,
    unix: dayjs(time, 'YYYYMMDD HHmmss').unix()
  }
}

/**
 * 获取文件后缀
 * @param name 文件名
 * @return {string}
 */
export function fetchSuffix(name) {
  return name.substring(name.lastIndexOf('.') + 1)
}

/**
 * 获取文件名（去除后缀）
 * @param name 文件名
 * @return {string}
 */
export function fetchFileName(name) {
  return name.split('.').slice(0, -1).join('.')
}

/**
 * 获取目录名
 * @param url Object 目录路径
 * @return {string}
 */
export function fetchDirectoryName(url) {
  return url.substring(url.lastIndexOf('/') + 1)
}

/**
 * 获取文件数据
 * @param fileUrl 文件路径
 * @return {any}
 */
export function fetchFile(fileUrl) {
  const data = fs.readFileSync(fileUrl, 'utf8')
  return data && JSON.parse(data)
}
